/**
 * Marking a level rubric (S3): a level per criterion at its exact points, an
 * award other than the level's only with that criterion's own reason, and the
 * marker's private draft saved to the server as journalled revisions. The tab
 * draft stays the editing state; the server draft follows it and outlives the tab.
 * A save's answer goes to the server draft's read, never into the tab draft, so
 * an answer arriving after the mark was recorded never begins another draft.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { a3Copy } from "@/components/assignments/a3-copy";
import { levelLines, levelPoints, levelTotal, pointsWord, type LevelAward } from "@/components/assignments/work/rubric-levels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { assignmentsApi, type RubricCriterion } from "@/lib/api/assignments";
import { ApiError } from "@/lib/api/client";
import { markDraftKey, trackMarkDraftSave } from "./mark-draft";
import type { ReviewDraft } from "./review-plan";

type MarkDocument = { levels: Record<string, string>; awards: Record<string, LevelAward>; comment: string };

const documentOf = (draft: Pick<ReviewDraft, "levels" | "awards" | "body">): string =>
  JSON.stringify({ levels: draft.levels ?? {}, awards: draft.awards ?? {}, comment: draft.body });

/** Saves the marker's private draft about a second after each change, and never while a send is out; says where it stands. */
function usePrivateMarkDraft(submissionId: string, draft: ReviewDraft, busy: boolean, onDraft: (change: Partial<ReviewDraft>) => void): string | null {
  const client = useQueryClient();
  const server = useQuery({ queryKey: markDraftKey(submissionId), queryFn: () => assignmentsApi.markDraft(submissionId), staleTime: Infinity });
  const [status, setStatus] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const revision = useRef<number | null>(null);
  const last = useRef<string | null>(null);
  const doc = documentOf(draft);
  useEffect(() => {
    if (revision.current != null || !server.data) return;
    const saved = server.data.draft;
    revision.current = saved?.revision ?? 0;
    // `last` is what the server holds, so anything entered before this read saves next.
    const stored = (saved?.document ?? {}) as Partial<MarkDocument>;
    const restored = { levels: stored.levels ?? {}, awards: stored.awards ?? {}, body: typeof stored.comment === "string" ? stored.comment : "" };
    last.current = documentOf(restored);
    if (saved && !saved.recorded && !Object.keys(draft.levels ?? {}).length) onDraft({ ...restored, outcome: "graded" });
    if (saved) setStatus(a3Copy.markDraftSaved(saved.revision));
    setReady(true);
  }, [server.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ready || busy || doc === last.current) return;
    const timer = setTimeout(() => {
      const save = (async () => {
        setStatus(a3Copy.markDraftSaving);
        try {
          const saved = await assignmentsApi.saveMarkDraft(submissionId, { expected_revision: revision.current!, document: JSON.parse(doc) });
          revision.current = saved.draft.revision;
          last.current = doc;
          client.setQueryData(markDraftKey(submissionId), saved);
          setStatus(a3Copy.markDraftSaved(saved.draft.revision));
        } catch (error) {
          if (error instanceof ApiError && error.status === 409) {
            // Another tab saved first: take its revision so the next change saves over it knowingly.
            const read = await assignmentsApi.markDraft(submissionId).catch(() => null);
            if (read) client.setQueryData(markDraftKey(submissionId), read);
            revision.current = read?.draft?.revision ?? revision.current;
          }
          setStatus(a3Copy.markDraftNotSaved(error instanceof Error ? error.message : String(error)));
        }
      })();
      trackMarkDraftSave(submissionId, save);
    }, 1000);
    return () => clearTimeout(timer);
  }, [doc, submissionId, ready, busy]); // eslint-disable-line react-hooks/exhaustive-deps
  return status;
}

export function LevelMarks({ rubric, points, draft, submissionId, busy, onDraft }: {
  rubric: RubricCriterion[]; points: number | null; draft: ReviewDraft; submissionId: string; busy: boolean;
  onDraft: (change: Partial<ReviewDraft>) => void;
}) {
  const levels = draft.levels ?? {}, awards = draft.awards ?? {};
  const lines = levelLines(rubric, levels, awards);
  const total = levelTotal(lines);
  const saved = usePrivateMarkDraft(submissionId, draft, busy, onDraft);
  const setAward = (id: string, change: Partial<LevelAward> | null) => {
    const next = { ...awards };
    if (change) next[id] = { ...(awards[id] ?? { points: "", reason: "" }), ...change };
    else delete next[id];
    onDraft({ awards: next });
  };
  return <fieldset data-review-levels className="grid gap-4">
    <legend className="mb-1 text-meta text-muted-foreground">{a3Copy.markedByLevel}</legend>
    {lines.map(line => {
      const criterion = line.criterion;
      const award = awards[criterion.id];
      const evidence = criterion.levels?.find(level => level.id === line.levelId)?.evidence;
      return <div key={criterion.id} data-review-criterion={criterion.id} className="grid min-w-0 gap-2">
        <p className="text-ui font-medium">{criterion.label} <span className="text-meta font-normal text-muted-foreground">{a3Copy.of}{pointsWord(Number(criterion.points))}</span></p>
        {criterion.description && <p dir="auto" className="text-meta text-muted-foreground">{criterion.description}</p>}
        <div role="radiogroup" aria-label={criterion.label} className="flex flex-wrap gap-x-4">
          {criterion.levels?.map(level => <label key={level.id} className="flex min-h-11 items-center gap-2 text-ui">
            <input type="radio" name={`level-${submissionId}-${criterion.id}`} className="size-4 accent-primary" checked={line.levelId === level.id} disabled={busy}
              onChange={() => { const next = { ...awards }; delete next[criterion.id]; onDraft({ levels: { ...levels, [criterion.id]: level.id }, awards: next }); }} />
            {level.name} <span className="text-meta text-muted-foreground">{pointsWord(levelPoints(Number(criterion.points), level.share))}</span></label>)}
        </div>
        {evidence && <p dir="auto" className="text-meta text-muted-foreground">{evidence}</p>}
        {line.levelId && <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-ui">{a3Copy.awardOtherPoints}
            <Input inputMode="decimal" className="w-24" aria-label={`${a3Copy.awardOtherPoints}: ${criterion.label}`} aria-invalid={line.problem && !line.adjusted ? true : undefined}
              value={award?.points ?? ""} disabled={busy} onChange={event => setAward(criterion.id, { points: event.target.value })} /></label>
          {award && <Button type="button" variant="subtle" size="sm" disabled={busy} onClick={() => setAward(criterion.id, null)}>{a3Copy.useTheLevelSPoints(line.levelName!, pointsWord(line.baseline!))}</Button>}
        </div>}
        {line.adjusted && <label className="grid gap-1 text-ui">{a3Copy.whyThisAward}
          <Textarea dir="auto" aria-label={`${a3Copy.whyThisAward}: ${criterion.label}`} aria-invalid={line.problem ? true : undefined} value={award?.reason ?? ""} disabled={busy}
            onChange={event => setAward(criterion.id, { reason: event.target.value })} /></label>}
        {line.levelId && line.problem && <p data-review-field-refusal className="text-meta text-status-danger">{line.problem}</p>}
      </div>;
    })}
    <p className="text-meta">{a3Copy.levelMarkTotal(total == null ? null : pointsWord(total), points)}</p>
    {saved && <p data-review-mark-draft className="text-meta text-muted-foreground">{saved}</p>}
  </fieldset>;
}
